import React from 'react';
import { useTopBrandsQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { PopularBrands } from '@/api/models';
import { useSales } from '@/pages/sales/SalesProvider';

/**
 * Returns a `PopularBrands` snapshot for the widget, scoped to the
 * page-level date range published through `SalesProvider`. Changing
 * the period filter on the page re-runs the underlying query because
 * `useTopBrandsQuery` keys on `from` / `to`.
 *
 * Mapping rules mirror the other Sales hooks:
 *
 *   - `isLoading` → `query.isPending` (true only on the first fetch).
 *   - `data` → the deserialized `PopularBrands` on success, `null` otherwise.
 *   - `error` → the `ErrorResponse` from the unsuccessful `ApiResponse`, or
 *     `null` on success. The queryFn never rejects (errors are folded
 *     into the response envelope by `makeApiRequest`).
 *   - `onRetry` → `query.refetch` adapted to a `() => void`.
 */
export const usePopularBrands = () => {
    const { dateRange } = useSales();
    const query = useTopBrandsQuery({
        from: dateRange.start,
        to: dateRange.end,
    });
    const { refetch } = query;

    const data = React.useMemo<PopularBrands | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
